import _ from 'lodash'
import React, { useContext, useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import SelectMultipleWithData from './SelectMultipleWithData'

//actions
import entityActions, { getFilterString } from '../../store/entity/actions'
import { sanitiseFilterString } from '../../libraries/sanitiseFilterString'
import { shouldRefetch } from './RadioList'
import PublicFormContext from '../../contexts/PublicFormContext'
import { evaluateRule } from '../query/evaluateRule'
import { flattenEntity } from '../../helpers/entity'
import { useServerFilter } from '../../hooks/useServerFilter'

const _SelectListMultiple = (props, ref) => {
  const { editable = true, lazy = false } = props
  const dispatch = useDispatch()
  const publicFormContext = useContext(PublicFormContext)

  const [nextPages, setNextPages] = useState({})

  const sourceNames = _.castArray(props.data.field.field_data.params.sourceName)

  const { serverFilter } = useServerFilter(
    sourceNames[0],
    props.data?.field?.field_data?.params?.serverFilter
  )

  const filterRule =
    props?.data?.field?.field_data?.params?.optionsFilter?.json?.logic

  const filter = filterRule ? { detailed: true } : null
  const filterString = sanitiseFilterString(
    getFilterString(serverFilter, filter)
  )

  const sourceListRoots = sourceNames.map((sourceName) => {
    return useSelector((state) =>
      sourceName
        ? _.get(state.entities, `[${sourceName + filterString}]`, {
            data: []
          })
        : []
    )
  })

  const fetchNextPage = (index) => {
    if (_.get(nextPages, index, false) !== -1) {
      let pageToFetch = nextPages[index]

      try {
        pageToFetch = sourceListRoots[index].meta.current_page + 1
      } catch (e) {
        // doesn't matter.
      }
      if (!pageToFetch || pageToFetch > sourceListRoots[index].meta.last_page) {
        pageToFetch = 1
      }

      dispatch(
        entityActions.listEntity(
          { name: sourceNames[index] },
          {
            filter: {
              ...filter,
              ...serverFilter
            },
            page: pageToFetch,
            detailed: filter?.detailed
          },
          undefined,
          publicFormContext.entityType,
          {
            tenantId: publicFormContext.tenantId,
            tenantHmac: publicFormContext.tenantHmac
          }
        )
      )
        .then((response) => {
          if (
            !nextPages[index] ||
            nextPages[index] <= response.meta.last_page
          ) {
            if (response.meta.current_page < response.meta.last_page) {
              setNextPages((nextPages) => ({
                ...nextPages,
                ...{ [index]: response.meta.current_page + 1 }
              }))
            } else {
              setNextPages((nextPages) => ({
                ...nextPages,
                ...{ [index]: -1 }
              }))
            }
          } else {
            setNextPages((nextPages) => ({ ...nextPages, ...{ [index]: -1 } }))
          }
        })
        .finally(() => {
          if (index === 0) {
            setIsLoadingInitial(false)
          }
        })
    }
  }

  const [dropdownIsOpen, setDropdownIsOpen] = useState(false)

  // used to determine when user is probably looking at a blank dropdown.
  const [isLoadingInitial, setIsLoadingInitial] = useState(false)

  useEffect(() => {
    if (!editable) {
      return
    }

    if (lazy && !dropdownIsOpen) {
      return
    }

    sourceListRoots.map((sourceListRoot, index) => {
      if (shouldRefetch(sourceListRoot)) {
        if (lazy && index == 0 && sourceListRoot?.data?.length == 0) {
          setIsLoadingInitial(true)
        }

        fetchNextPage(index)
      } else {
        setNextPages((nextPages) => ({ ...nextPages, ...{ [index]: 1 } }))
      }
    })
  }, [editable, lazy, dropdownIsOpen, serverFilter])

  const sourceData = useMemo(() => {
    const data = _.flatten(
      sourceListRoots.map((sourceListRoot) => {
        return _.get(sourceListRoot, 'data', [])
      })
    )

    const filteredData = filterRule
      ? data.filter((d) =>
          evaluateRule(filterRule, {
            ...flattenEntity(d, false),
            field: flattenEntity(props.data.entity, false)
          })
        )
      : data

    return filteredData
  }, [sourceListRoots])

  const groupByFunction =
    sourceListRoots.length > 1
      ? (f) => {
          return f.entity_type?.label
        }
      : undefined

  const sortByFunction = (el) => ('' + el.display_name).toLowerCase()

  return (
    <SelectMultipleWithData
      {...props}
      dataRows={sourceData}
      groupByFunction={groupByFunction}
      sortByFunction={sortByFunction}
      isLoadingInitial={isLoadingInitial}
      labelField={'display_name'}
      onDropdownStatusChanged={(showDropDown) => {
        setDropdownIsOpen(showDropDown)
      }}
      onEndReached={() => {
        if (!editable) {
          return
        }
        // console.log("End reached.", sourceListRoots);
        sourceListRoots.map((sourceListRoot, index) => {
          fetchNextPage(index)
        })
      }}
      value={props.defaultValue}
    />
  )
}
export const SelectListMultiple = React.forwardRef(_SelectListMultiple)
